import { useEffect } from "react";
import { MobileDecisionDialog } from "./MobileDecisionDialog.tsx";
import { getOwnerLetterDecisionCta, OwnerLetterContent, useOwnerLetter } from "./OwnerLetter.tsx";
import i18n from "../util/i18n.ts";
import { translateMessageSubject } from "../util/messageTranslations.ts";

// =============================================================================
// G4-2: a pending owner letter shown in the "决定" container (MobileDecisionDialog).
// Reached when the home task queue's inbox decision item is tapped and an unread
// decision letter exists, or via the ?m_open_latest deep link — the letter is
// pushed straight to the player as a centered card (subject = title, body +
// owner-trust chart + 落款), instead of first opening the inbox list and drilling
// in. The single main action is the letter's business CTA for a decision letter
// (NAVIGATE exit); a non-decision letter shows no action, only the header X. The
// letter body/fetch is the shared OwnerLetter piece — same code as the InboxSheet
// level-2, so the two containers never drift.
// =============================================================================

export const OwnerLetterDialog = ({
  mid,
  onClose,
  onMessageRead,
  open,
}: {
  mid?: number;
  onClose: () => void;
  onMessageRead?: (mid: number) => void;
  open: boolean;
}) => {
  const letter = useOwnerLetter(mid, open);

  useEffect(() => {
    if (open && mid !== undefined) {
      onMessageRead?.(mid);
    }
  }, [mid, onMessageRead, open]);

  const title =
    letter && letter.subject
      ? (translateMessageSubject(letter.subject) ?? letter.subject)
      : i18n.t("message.notFoundTitle", {
          ns: "ui",
          defaultValue: "Message not found",
        });

  const cta = letter ? getOwnerLetterDecisionCta(letter) : undefined;
  const action = cta ? (
    <a className="btn btn-primary w-100" data-owner-letter-dialog-decision-cta="" href={cta.href}>
      {i18n.t(cta.labelKey, { ns: "ui", defaultValue: cta.defaultLabel })}
    </a>
  ) : undefined;

  return (
    <MobileDecisionDialog action={action} onClose={onClose} open={open} title={title}>
      {letter === undefined ? (
        <div className="d-flex justify-content-center py-4" data-owner-letter-dialog-loading="">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">
              {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
            </span>
          </div>
        </div>
      ) : (
        <OwnerLetterContent message={letter ?? undefined} />
      )}
    </MobileDecisionDialog>
  );
};

export default OwnerLetterDialog;
